import React from 'react';

import PropTypes from 'prop-types';
import {useSnackbar} from 'notistack';
import {Form, FormikProps} from 'formik';
import {CallMade} from '@material-ui/icons';
import {Box, Grid} from '@material-ui/core';

import {
    EfeResponse,
    SepomexCatalog,
    EfeCatalogValue,
    AddressServiceRequest,
    SepomexCatalogResponse,
} from 'commons/models';
import {Status, Messages, SnackbarVariants} from 'commons/constants';
import {FieldShape} from 'commons/shapes';
import {useSpinner} from 'hooks';
import {InferPropTypes} from 'commons/types/Types';
import {CatalogServices} from 'services';

import {EfeButtonPrimary, EfeFormSection, EfeSupportMessage} from 'components';

AddressInnerForm.propTypes = {
    viewId: PropTypes.number.isRequired,
    viewKey: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};

function AddressInnerForm(
    props: InferPropTypes<typeof AddressInnerForm.propTypes & FormikProps<AddressServiceRequest>>
) {
    const {
        values,
        errors,
        fields,

        isSubmitting,
        setFieldValue,
    } = props;

    const {enqueueSnackbar} = useSnackbar();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    function resetValues() {
        setFieldValue('municipalityKey', '');
        setFieldValue('municipalityDescription', '');
        setFieldValue('stateKey', '');
        setFieldValue('stateDescription', '');
        setFieldValue('countryKey', '');
    }

    function setCatalogValues(
        data: Array<SepomexCatalog>,
        zipCode: string,
        colonyKey: number,
        stateKey: number
    ) {
        const colony: Array<EfeCatalogValue> = [];
        const state: Array<EfeCatalogValue> = [];
        let colonyItem: EfeCatalogValue;
        let stateItem: EfeCatalogValue;
        data.map((item: SepomexCatalog, index: number) => {
            if (!colonyItem || colonyItem.id !== item.settlementCpconsId) {
                colonyItem = {
                    id: item.settlementCpconsId,
                    order: index,
                    value: item.settlement,
                    status: Status.Active,
                };
                colony.push({...colonyItem});
            }
            if (!stateItem || stateItem.id !== item.stateKey) {
                stateItem = {
                    id: item.stateKey,
                    order: index,
                    value: item.state,
                    status: Status.Active,
                };
                state.push({...stateItem});
            }
        });
        if (fields && fields.length) {
            fields[colonyKey].catalogValues = [...colony];
            fields[stateKey].catalogValues = [...state];
            if (data && data.length > 0) {
                setValues(data[0], zipCode);
            }
        }
    }
    function setValues(data: SepomexCatalog, zipCode: string) {
        if (zipCode) {
            setFieldValue('zipCode', zipCode);
        }
        if (data && data.municipalityKey) {
            setFieldValue('municipalityKey', data.municipalityKey);
        }
        if (data && data.municipality) {
            setFieldValue('municipalityDescription', data.municipality);
        }
        if (data && data.stateKey) {
            setFieldValue('stateKey', data.stateKey);
        }
        if (data && data.state) {
            setFieldValue('stateDescription', data.state);
        }
    }

    async function searchZipCode(value: string, colonyKey: number, stateKey: number) {
        if (value && value.length === 5) {
            setIsLoading(true);
            try {
                const sepomexData: EfeResponse<SepomexCatalogResponse> =
                    await CatalogServices.getSepomexCatalog(value);
                if (sepomexData.data && sepomexData.data.data && sepomexData.data.data.length > 0) {
                    setCatalogValues([...sepomexData.data.data], value, colonyKey, stateKey);
                } else {
                    setCatalogValues([], value, colonyKey, stateKey);
                    resetValues();
                    enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
                setIsLoading(false);
            } catch (error) {
                resetValues();
                setIsLoading(false);
                setCatalogValues([], value, colonyKey, stateKey);
                enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        }
    }

    React.useEffect(() => {
        const colonyKey = fields.findIndex((field) => field.attrName === 'colonyKey');
        const stateKey = fields.findIndex((field) => field.attrName === 'stateKey');
        if (values.zipCode && values.zipCode.length === 5 && colonyKey >= 0 && stateKey >= 0) {
            searchZipCode(values.zipCode, colonyKey, stateKey);
        }
    }, [values.zipCode, fields]);

    /* eslint-disable react/jsx-handler-names */
    return (
        <Form noValidate>
            {isLoading && useSpinner(isLoading)}
            {fields && fields.length > 0 && (
                <Box m={2}>
                    <EfeSupportMessage />
                    <EfeFormSection
                        fields={fields}
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                    />
                    <Grid container direction='row' justifyContent='flex-end'>
                        <Box pt={6} width={160}>
                            <EfeButtonPrimary
                                fullWidth
                                disabled={isSubmitting}
                                type='submit'
                                endIcon={<CallMade />}>
                                {'OK'}
                            </EfeButtonPrimary>
                        </Box>
                    </Grid>
                </Box>
            )}
        </Form>
    );
}

export default AddressInnerForm;
